import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("div", null, [
  /*#__PURE__*/_createElementVNode("div", { class: "title" }, "ご確認ください")
], -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "quick-response-addition-template_content" }, [
  /*#__PURE__*/_createElementVNode("p", { class: "quick-response-addition-template-confirm__description" }, " 今回の設定を今後の小ロットOEM商品にも適用しますか？ ")
], -1)

import { ref } from 'vue'
import { useStore } from 'vuex'
import { t } from '@/plugins/i18n'
import { request } from '@/config/request/interceptors'
import { COUNTRY } from '@/global'
import { getWarehouseByCountry, isD2C } from '@/store'
import { tCurrency } from '@/views/shopcart/country'
import type {
  AdditionTemplateItemRespDTO,
  CustomerAdditionTemplateAddReqDTO,
  CustomerAdditionTemplateItemAddReqDTO,
  QuickResponseAdditionTemplateRespDTO
} from '@/service/settlement'

/** 小单快反附加项模板保存入参，补充生成类型暂未包含的业务字段。 */
interface QuickResponseAdditionTemplatePayload extends CustomerAdditionTemplateAddReqDTO {
  /** 小单快反模板固定为非一价全包。 */
  priceType?: boolean
  /** 默认模板来源。 */
  templateSource?: string
}

/** 当前选择的附加项，补充弹窗展示所需字段。 */
interface QuickResponseSelectedAddition extends CustomerAdditionTemplateItemAddReqDTO {
  /** 附加项名称。 */
  additionName?: string
  /** 附加项日文名称。 */
  additionNameJp?: string
  /** 附加项扩展名称。 */
  additionExtraName?: string
  /** 附加项图片。 */
  imageUrl?: string
  /** 附加项动态图。 */
  gifUrl?: string
  /** 附加项图片兼容字段。 */
  additionImageUrl?: string
  /** 附加项图标兼容字段。 */
  icon?: string
  cost?:number
  calculateConfigId?:number
  currencyCode?:string
}

/** 弹窗展示的附加项合并数据。 */
interface QuickResponseAdditionDisplayItem extends QuickResponseSelectedAddition {
  /** 最终用于展示的附加项图片。 */
  imageUrl?: string
}

/** 小单快反商品附加项模板类型。 */

export default /*#__PURE__*/_defineComponent({
  setup(__props, { expose }) {

const QUICK_RESPONSE_ADDITION_TEMPLATE_TYPE = 1
/** 小单快反默认模板名称兜底。 */
const QUICK_RESPONSE_ADDITION_TEMPLATE_NAME = 'OEM小批量生产'
/** 当前 Vuex store。 */
const store = useStore()
/** 确认弹窗展示状态。 */
const visible = ref(false)
/** 默认模板保存状态。 */
const saving = ref(false)
/** 当前小单快反默认附加项模板。 */
const defaultTemplate = ref<QuickResponseAdditionTemplateRespDTO>()
/** 本次选择的附加项快照。 */
const selectedAdditionList = ref<QuickResponseSelectedAddition[]>([])
/** 弹窗展示的附加项列表。 */
const additionDisplayList = ref<QuickResponseAdditionDisplayItem[]>([])

/** 获取当前店铺 ID。 */
const getCurrentCustomerShopId = () => {
  /** 转换后的当前店铺 ID。 */
  const customerShopId = Number(store.getters.curShop)
  return Number.isFinite(customerShopId) ? customerShopId : undefined
}

/** 获取标准化后的附加项配置 ID 集合，用于忽略顺序和重复项比较。 */
const getAdditionConfigIdSet = (
  additionList?: Array<{ additionConfigId?: number | string }>
) => {
  /** 统一转为字符串并去重后的附加项配置 ID 集合。 */
  const additionConfigIdSet = new Set(
    additionList
      ?.map(item => item.additionConfigId)
      .filter((id): id is number | string => {
        return id !== undefined && id !== null && String(id) !== ''
      })
      .map(id => String(id)) ?? []
  )

  return additionConfigIdSet
}

/** 判断本次选择是否与小单快反默认模板不同。 */
const getHasDifferentAdditionConfig = (
  selectedAddition: QuickResponseSelectedAddition[],
  templateItems?: AdditionTemplateItemRespDTO[]
) => {
  /** 本次选择的附加项配置 ID 集合。 */
  const selectedConfigIdSet = getAdditionConfigIdSet(selectedAddition)
  /** 默认模板的附加项配置 ID 集合。 */
  const templateConfigIdSet = getAdditionConfigIdSet(templateItems)

  if (selectedConfigIdSet.size !== templateConfigIdSet.size) return true

  return Array.from(selectedConfigIdSet).some(configId => {
    return !templateConfigIdSet.has(configId)
  })
}

/** 合并本次选择与默认模板中的附加项展示字段。 */
const getAdditionDisplayList = (
  selectedAddition: QuickResponseSelectedAddition[],
  templateItems?: AdditionTemplateItemRespDTO[]
) => {
  return selectedAddition.map(selectedItem => {
    /** 默认模板中与本次选择匹配的附加项。 */
    const templateItem = templateItems?.find(item => {
      if (
        typeof selectedItem.additionConfigId === 'number' &&
        typeof item.additionConfigId === 'number'
      ) {
        return selectedItem.additionConfigId === item.additionConfigId
      }

      return selectedItem.additionId === item.additionId
    })
    /** 当前附加项展示图片。 */
    const imageUrl = selectedItem.imageUrl ??
      selectedItem.gifUrl ??
      selectedItem.additionImageUrl ??
      selectedItem.icon ??
      templateItem?.imageUrl

    return {
      ...templateItem,
      ...selectedItem,
      additionNameJp: selectedItem.additionNameJp ?? templateItem?.additionNameJp,
      additionName: selectedItem.additionName ?? templateItem?.additionName,
      ingredientsNameJp: selectedItem.ingredientsNameJp ?? templateItem?.ingredientsNameJp,
      ingredientsName: selectedItem.ingredientsName ?? templateItem?.ingredientsName,
      imageUrl
    }
  })
}

/** 获取附加项展示名称。 */
const getAdditionName = (item: QuickResponseAdditionDisplayItem) => {
  return item.additionNameJp ??
    item.additionName ??
    item.additionExtraName ??
    item.ingredientsNameJp ??
    item.ingredientsName ??
    '-'
}

/** 获取附加项列表渲染 key。 */
const getAdditionKey = (item: QuickResponseAdditionDisplayItem, index: number) => {
  return item.additionConfigId ?? item.additionId ?? `${getAdditionName(item)}-${index}`
}

/** 生成保存小单快反默认附加项模板的请求参数。 */
const getAdditionTemplatePayload = (
  template: QuickResponseAdditionTemplateRespDTO,
  selectedAddition: QuickResponseSelectedAddition[]
) => {
  /** 默认模板是否已有附加项明细。 */
  const hasDefaultTemplateItems = Boolean(template.items?.length)
  /** 默认模板保存请求参数。 */
  const payload: QuickResponseAdditionTemplatePayload = {
    templateName: template.templateName ?? QUICK_RESPONSE_ADDITION_TEMPLATE_NAME,
    templateType: QUICK_RESPONSE_ADDITION_TEMPLATE_TYPE,
    templateScene: template.templateScene,
    templateSource: template.templateSource,
    remark: '',
    priceType: false,
    supportAllInclude: false,
    reqDTOList: selectedAddition,
    warehouseCode: getWarehouseByCountry(COUNTRY.CN)
  }

  if (hasDefaultTemplateItems) {
    payload.additionTemplateId = template.additionTemplateId
  }

  return payload
}

/** 查询差异并按需打开小单快反默认模板确认弹窗。 */
const checkAndOpen = async (selectedAddition: QuickResponseSelectedAddition[] | 1) => {
  if (selectedAddition === 1 || !Array.isArray(selectedAddition)) return

  /** 本次选择的附加项快照。 */
  const nextSelectedAddition = selectedAddition.map(item => ({ ...item }))
  /** 当前系统来源。 */
  const systemSource = isD2C() ? 1 : 2

  try {
    /** 小单快反默认附加项模板接口响应。 */
    const res = await request.settlement.addition.customerTemplateDefault({
      customerShopId: getCurrentCustomerShopId(),
      systemSource
    } as any)
    /** 接口返回的小单快反默认附加项模板。 */
    const nextDefaultTemplate = res.data

    if (
      !nextDefaultTemplate ||
      !getHasDifferentAdditionConfig(nextSelectedAddition, nextDefaultTemplate.items)
    ) return

    defaultTemplate.value = nextDefaultTemplate
    selectedAdditionList.value = nextSelectedAddition
    additionDisplayList.value = getAdditionDisplayList(
      nextSelectedAddition,
      nextDefaultTemplate.items
    )
    visible.value = true
  } catch {
    // 默认模板查询失败不影响订单附加项本身的保存结果。
  }
}

/** 关闭确认弹窗。 */
const handleCancel = () => {
  if (saving.value) return
  visible.value = false
}

/** 处理弹窗右上角关闭操作。 */
const handleBeforeClose = (done: () => void) => {
  if (saving.value) return
  done()
}

/** 保存本次选择为小单快反默认附加项模板。 */
const handleConfirm = async () => {
  if (!defaultTemplate.value || saving.value) return

  saving.value = true
  try {
    /** 默认模板是否已有附加项明细。 */
    const hasDefaultTemplateItems = Boolean(defaultTemplate.value.items?.length)
    /** 默认附加项模板保存参数。 */
    const payload = getAdditionTemplatePayload(
      defaultTemplate.value,
      selectedAdditionList.value
    )
    /** 默认附加项模板保存接口响应。 */
    const res = hasDefaultTemplateItems
      ? await request.settlement.addition.customerTemplateUpdateCustomerAdditionTemplate(payload)
      : await request.settlement.addition.customerTemplateAddCustomerAdditionTemplate(payload)

    await request.settlement.addition.customerTemplateUpdateCustomerAdditionTemplateStatus(
      {
        additionTemplateId: defaultTemplate.value.additionTemplateId,
        templateStatus: 1
      }
    )
    if (res.success) {
      visible.value = false
    }
  } finally {
    saving.value = false
  }
}

expose({ checkAndOpen })

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: visible.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((visible).value = $event)),
    title: "ご確認ください",
    width: "624px",
    top: "15vh",
    "append-to-body": "",
    "destroy-on-close": "",
    "custom-class": "quick-response-addition-template-confirm",
    "close-on-click-modal": false,
    "close-on-press-escape": !saving.value,
    "before-close": handleBeforeClose
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, {
        disabled: saving.value,
        onClick: handleCancel
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('取消')), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_el_button, {
        type: "primary",
        loading: saving.value,
        onClick: handleConfirm
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(t)('确定')), 1)
        ]),
        _: 1
      }, 8, ["loading"])
    ]),
    default: _withCtx(() => [
      _hoisted_1,
      _hoisted_2
    ]),
    _: 1
  }, 8, ["modelValue", "close-on-press-escape"]))
}
}

})